import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { API_URL } from '../../utils/api';
import './AdminSettings.css';

/**
 * Discord-kanaler exponerade i admin-UI:t.
 *
 * Team1/Team2 togs bort i PR R (2026-05-25) — bot:en skapar dynamiska
 * match-VCs under ``match_category`` per match istället för att flytta till
 * permanenta team-kanaler. Stats/queue/tournament-channel är legacy
 * textkanaler; bot:en postar inte i dem efter web-first paradigmet men
 * fälten finns kvar för bakåtkompatibilitet.
 *
 * i18n: modul-konstanter (ingen hook-scope) → `labelKey`/`descKey` håller
 * översättningsnycklar som resolvas via `t()` i render-komponenten. Brand-/
 * tekniska val behåller `label` (renderas `opt.labelKey ? t(opt.labelKey) : opt.label`).
 */
const CHANNEL_FIELDS = [
  { key: 'match_category', labelKey: 'admin.chMatchCategory', descKey: 'admin.chMatchCategoryDesc' },
  { key: 'lobby_channel', labelKey: 'admin.chLobby', descKey: 'admin.chLobbyDesc' },
  { key: 'stats_channel', labelKey: 'admin.chStats', descKey: 'admin.chStatsDesc' },
  { key: 'queue_channel', labelKey: 'admin.chQueue', descKey: 'admin.chQueueDesc' },
  { key: 'tournament_channel', labelKey: 'admin.chTournament', descKey: 'admin.chTournamentDesc' },
];

// Fasta val for value_type='str' SiteConfig-flaggor. Saknas en nyckel har
// blir det en read-only visning eller fri text-input (om i FREE_TEXT_KEYS).
const STRING_OPTIONS = {
  CHAT_LLM_PROVIDER: [
    { value: 'auto', labelKey: 'admin.llmAuto' },
    { value: 'anthropic', label: 'Anthropic (Claude Haiku)' },
    { value: 'gemini', label: 'Gemini (2.5 Flash)' },
    { value: 'ollama', label: 'Ollama (lokal)' },
  ],
  HIGHLIGHT_COMMENT_TONE: [
    { value: 'hype', labelKey: 'admin.toneHype' },
    { value: 'fun', labelKey: 'admin.toneFun' },
  ],
  BACKFILL_LEAVER_POLICY: [
    { value: 'exclude', label: 'Undanta (ingen ELO)' },
    { value: 'loss', label: 'Räkna som förlust' },
  ],
  // Upplösning för highlights resp. streaming (togglas var för sig). Kräver att
  // X:99-framebuffern är minst den valda upplösningen (host-X-config).
  RENDER_RESOLUTION: [
    { value: '1080p', label: '1080p (1920×1080)' },
    { value: '1440p', label: '1440p (2560×1440)' },
  ],
  STREAM_RESOLUTION: [
    { value: '1080p', label: '1080p (1920×1080)' },
    { value: '1440p', label: '1440p (2560×1440)' },
  ],
  // Streaming-mål: native lokal agent (alltid-på dator) eller AWS EC2-instans.
  STREAMING_TARGET: [
    { value: 'local', label: 'Local (native agent)' },
    { value: 'aws', label: 'AWS (EC2-instans)' },
  ],
  // Matchtips: när tips-fördelningen syns för spelarna (låset vid GOING_LIVE
  // visar den alltid).
  PREDICTIONS_VISIBILITY: [
    { value: 'hidden', label: 'Dold tills matchstart' },
    { value: 'open', label: 'Öppen hela tiden' },
  ],
};

// value_type='str' nycklar dar fri textinmatning ar onskvard (t.ex. komma-
// separerad lista). Validering sker i backend.
const FREE_TEXT_KEYS = new Set(['CHAT_LLM_FALLBACK_CHAIN']);

const SETTINGS_TABS = [
  { key: 'general', labelKey: 'admin.tabGeneral' },
  { key: 'voice', labelKey: 'admin.tabVoice' },
  { key: 'features', labelKey: 'admin.tabFeatures' },
];

// Ordning på kategorirubrikerna. Feature-flaggorna ligger först eftersom de
// togglas oftast; ELO-parametrarna sist eftersom de sällan rörs och ändrar
// rankingen retroaktivt först vid omräkning.
const CATEGORY_ORDER = ['features', 'general', 'veto', 'drops', 'elo'];

/**
 * Grupperar SiteConfig-rader per kategori i CATEGORY_ORDER-ordning.
 *
 * Backend började returnera fler kategorier än ``features`` (ELO, veto,
 * drops), och en platt lista på 65 rader är obrukbar — ELO_K_NORMAL skulle
 * ligga inklämd mellan två orelaterade feature-flaggor.
 *
 * @param {Array<{key: string, category?: string}>} flags - Rader från API:t.
 * @returns {Array<[string, Array<object>]>} Par av kategorinamn och dess rader.
 */
function groupByCategory(flags) {
  const groups = new Map();
  flags.forEach((f) => {
    const cat = f.category || 'features';
    if (!groups.has(cat)) groups.set(cat, []);
    groups.get(cat).push(f);
  });
  // Okända kategorier hamnar sist i bokstavsordning i stället för att
  // försvinna — en ny kategori i backend ska synas utan frontend-ändring.
  return [...groups.entries()].sort(([a], [b]) => {
    const ia = CATEGORY_ORDER.indexOf(a);
    const ib = CATEGORY_ORDER.indexOf(b);
    if (ia === -1 && ib === -1) return a.localeCompare(b);
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    return ia - ib;
  });
}

/**
 * Värdet som text i redigeringsfältet.
 *
 * JSON-rader kommer som objekt från API:t; String() på ett objekt ger
 * "[object Object]", så drop-vikterna gick varken att läsa eller spara.
 *
 * @param {object} flag - SiteConfig-raden ({ value, value_type })
 * @returns {string} Texten fältet ska visa
 */
function flagToDraft(flag) {
  if (flag.value_type === 'json') return JSON.stringify(flag.value ?? null, null, 2);
  return String(flag.value ?? '');
}

/**
 * En rad i feature-flag-listan. Renderar olika kontroll beroende pa typ:
 * - bool: PA/AV-knapp (toggle direkt)
 * - str med STRING_OPTIONS-mappning: dropdown som sparar pa change
 * - int: number-input + Spara-knapp
 * - ovrigt: read-only visning av varde
 */
function FeatureFlagRow({ flag, onToggle, onSave }) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(flagToDraft(flag));
  const [busy, setBusy] = useState(false);

  // Synka lokalt utkast om backend-vardet ändras (t.ex. efter optimistisk rollback).
  useEffect(() => {
    setDraft(flagToDraft(flag));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flag.value]);

  const renderControl = () => {
    if (flag.value_type === 'bool') {
      return (
        <button
          className="admin-btn"
          onClick={() => onToggle(flag.key, flag.value)}
          style={{
            minWidth: 70,
            background: flag.value ? '#4ade80' : 'rgba(255,255,255,0.1)',
            color: flag.value ? '#111' : '#aaa',
          }}
        >
          {flag.value ? t('veto.on') : t('veto.off')}
        </button>
      );
    }

    if (flag.value_type === 'str' && STRING_OPTIONS[flag.key]) {
      return (
        <select
          className="admin-input"
          value={String(flag.value ?? '')}
          onChange={async (e) => {
            const next = e.target.value;
            setBusy(true);
            await onSave(flag.key, next, flag.value);
            setBusy(false);
          }}
          disabled={busy}
          style={{ minWidth: 180 }}
        >
          {STRING_OPTIONS[flag.key].map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.labelKey ? t(opt.labelKey) : opt.label}
            </option>
          ))}
        </select>
      );
    }

    if (flag.value_type === 'str' && FREE_TEXT_KEYS.has(flag.key)) {
      const dirty = draft !== String(flag.value ?? '');
      return (
        <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
          <input
            className="admin-input"
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            disabled={busy}
            style={{ width: 220, fontFamily: 'monospace', fontSize: '0.8rem' }}
          />
          <button
            className="admin-btn"
            onClick={async () => {
              setBusy(true);
              await onSave(flag.key, draft, flag.value);
              setBusy(false);
            }}
            disabled={busy || !dirty}
          >
            {t('settings.save')}
          </button>
        </div>
      );
    }

    if (flag.value_type === 'int' || flag.value_type === 'float') {
      const isFloat = flag.value_type === 'float';
      const parse = (s) => (isFloat ? parseFloat(s) : parseInt(s, 10));
      const parsed = parse(draft);
      const dirty = draft !== String(flag.value ?? '');
      return (
        <div className="cfg-control">
          <input
            className="admin-input cfg-number"
            type="number"
            step={isFloat ? '0.05' : '1'}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            disabled={busy}
          />
          <button
            className="admin-btn"
            onClick={async () => {
              if (Number.isNaN(parsed)) return;
              setBusy(true);
              await onSave(flag.key, parsed, flag.value);
              setBusy(false);
            }}
            disabled={busy || !dirty || Number.isNaN(parsed)}
          >
            {t('settings.save')}
          </button>
        </div>
      );
    }

    if (flag.value_type === 'json') {
      // JSON valideras i klienten innan spar — backend svarar visserligen 400
      // på trasig JSON, men ett rött fält direkt är snabbare än en rundtur,
      // och drop-vikterna är lätta att slarva ett kommatecken i.
      let jsonError = false;
      try {
        JSON.parse(draft);
      } catch {
        jsonError = true;
      }
      const dirty = draft !== flagToDraft(flag);
      return (
        <div className="cfg-control cfg-control-stacked">
          <textarea
            className={`admin-input cfg-json${jsonError ? ' cfg-json-invalid' : ''}`}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            disabled={busy}
            rows={3}
            spellCheck={false}
          />
          <div className="cfg-json-actions">
            {jsonError && <span className="admin-error">{t('admin.cfgInvalidJson')}</span>}
            <button
              className="admin-btn"
              onClick={async () => {
                setBusy(true);
                await onSave(flag.key, JSON.parse(draft), flag.value);
                setBusy(false);
              }}
              disabled={busy || !dirty || jsonError}
            >
              {t('settings.save')}
            </button>
          </div>
        </div>
      );
    }

    return (
      <span className="admin-value" style={{ fontFamily: 'monospace' }}>
        {String(flag.value)}
      </span>
    );
  };

  return (
    <div className="admin-row">
      <label
        className="admin-label"
        title={flag.description || flag.key}
        style={{ flex: 1 }}
      >
        <div style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{flag.key}</div>
        {flag.description && (
          <div style={{ color: '#888', fontSize: '0.75rem', marginTop: 2 }}>
            {flag.description}
          </div>
        )}
      </label>
      {renderControl()}
    </div>
  );
}

/**
 * Generisk accordion-wrapper för voice-platform-sektioner.
 *
 * Header med titel + chevron + valfri "ACTIVE"-badge. Klick togglar.
 * Inbäddade ``admin-card``-element i barn-komponenten får sin ram
 * borttagen via CSS så att accordion-bodyn agerar som kort-ram.
 */
function AccordionCard({ title, active, defaultOpen, children }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={`admin-accordion ${open ? 'open' : ''}`}>
      <button
        type="button"
        className="admin-accordion-header"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <span className="admin-accordion-chevron">▶</span>
        <span className="admin-accordion-title">{title}</span>
        {active && <span className="admin-accordion-badge">{t('admin.pillActive')}</span>}
      </button>
      {open && <div className="admin-accordion-body">{children}</div>}
    </div>
  );
}

/**
 * Admin panel: guild settings configuration.
 *
 * Visar guild-config + voice-platform-konfiguration uppdelat på tre sub-tabs:
 * Allmänt (guild + bot-invite), Voice (Discord/TS3/Fluxer som accordion-kort),
 * Funktioner (feature-flags + demo-verktyg, owner-gated).
 *
 * Aktiv platform (`guild.voice_platform`) får sin accordion default-expanderad
 * så det är tydligt vilken sektion som matter på just denna installation.
 */
export default function AdminSettings({ perms }) {
  const { t } = useTranslation();
  const { getAuthHeaders } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = SETTINGS_TABS.some((st) => st.key === searchParams.get('tab'))
    ? searchParams.get('tab')
    : 'general';
  const setActiveTab = (key) => setSearchParams({ tab: key }, { replace: true });

  const [guild, setGuild] = useState(null);
  const [loading, setLoading] = useState(true);
  const [features, setFeatures] = useState(null);
  const [featuresError, setFeaturesError] = useState('');

  const fetchGuild = useCallback(async () => {
    if (!perms.guildId) return;
    try {
      const r = await fetch(`${API_URL}/api/admin/guild/${perms.guildId}`, {
        headers: getAuthHeaders(),
      });
      if (r.ok) {
        setGuild(await r.json());
      }
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [perms.guildId, getAuthHeaders]);

  useEffect(() => {
    fetchGuild();
  }, [fetchGuild]);

  const fetchFeatures = useCallback(async () => {
    if (!perms.isOwner) return;
    try {
      const r = await fetch(`${API_URL}/api/admin/site-config/features`, {
        headers: getAuthHeaders(),
      });
      if (r.ok) {
        setFeatures(await r.json());
      } else {
        setFeaturesError(t('admin.featuresLoadError'));
      }
    } catch {
      setFeaturesError(t('admin.featuresNetworkError'));
    }
  }, [perms.isOwner, getAuthHeaders, t]);

  useEffect(() => {
    fetchFeatures();
  }, [fetchFeatures]);

  /**
   * Spara ett nytt SiteConfig-värde och uppdatera lokalt state optimistiskt.
   * Återställer förra värdet om PATCH:en fallerar.
   */
  const saveFeature = async (key, nextValue, prevValue) => {
    setFeaturesError('');
    setFeatures((prev) =>
      (prev || []).map((f) => (f.key === key ? { ...f, value: nextValue } : f))
    );
    try {
      const r = await fetch(`${API_URL}/api/admin/site-config/${key}`, {
        method: 'PATCH',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ value: nextValue }),
      });
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        setFeaturesError(data.error || data.detail || t('settings.couldNotSave'));
        setFeatures((prev) =>
          (prev || []).map((f) => (f.key === key ? { ...f, value: prevValue } : f))
        );
        return false;
      }
      return true;
    } catch {
      setFeaturesError(t('settings.networkError'));
      setFeatures((prev) =>
        (prev || []).map((f) => (f.key === key ? { ...f, value: prevValue } : f))
      );
      return false;
    }
  };

  const toggleFeature = (key, currentValue) =>
    saveFeature(key, !currentValue, currentValue);

  if (loading) {
    return (
      <div>
        <h1 className="admin-section-title">{t('admin.navSettings')}</h1>
        <div className="admin-info">{t('live.loading')}</div>
      </div>
    );
  }

  if (!guild) {
    return (
      <div>
        <h1 className="admin-section-title">{t('admin.navSettings')}</h1>
        <div className="admin-error">{t('admin.guildLoadError')}</div>
      </div>
    );
  }

  const activePlatform = guild.voice_platform || 'discord';

  return (
    <div>
      <h1 className="admin-section-title">{t('admin.navSettings')}</h1>

      <div className="admin-tabs">
        {SETTINGS_TABS.map((st) => {
          if (st.key === 'features' && !perms.isOwner) return null;
          return (
            <button
              key={st.key}
              className={`admin-tab ${activeTab === st.key ? 'active' : ''}`}
              onClick={() => setActiveTab(st.key)}
            >
              {t(st.labelKey)}
            </button>
          );
        })}
      </div>

      {activeTab === 'general' && (
        <>
          <div className="admin-card">
            <div className="admin-card-title">Guild</div>
            <div className="admin-row">
              <span className="admin-label">{t('admin.nameLabel')}</span>
              <span className="admin-value">{guild.name || '-'}</span>
            </div>
            <div className="admin-row">
              <span className="admin-label">Guild ID</span>
              <span className="admin-value" style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                {guild.guild_id || '-'}
              </span>
            </div>
          </div>

          <VoicePlatformSection
            guild={guild}
            guildId={perms.guildId}
            getAuthHeaders={getAuthHeaders}
            onSaved={(updated) => setGuild(updated)}
          />

          <BotInviteSection getAuthHeaders={getAuthHeaders} />
        </>
      )}

      {activeTab === 'voice' && (
        <>
          <div className="admin-card-desc" style={{ marginBottom: '0.75rem' }}>
            {t('admin.voiceTabDesc')}
          </div>

          <AccordionCard
            title="Discord"
            active={activePlatform === 'discord'}
            defaultOpen={activePlatform === 'discord'}
          >
            <DiscordConfigSection
              guild={guild}
              guildId={perms.guildId}
              getAuthHeaders={getAuthHeaders}
              onSaved={setGuild}
            />
          </AccordionCard>

          <AccordionCard
            title="TeamSpeak 3"
            active={activePlatform === 'teamspeak'}
            defaultOpen={activePlatform === 'teamspeak'}
          >
            <TS3ConfigSection
              guild={guild}
              guildId={perms.guildId}
              getAuthHeaders={getAuthHeaders}
              onSaved={setGuild}
            />
          </AccordionCard>

          <AccordionCard
            title="Fluxer"
            active={activePlatform === 'fluxer'}
            defaultOpen={activePlatform === 'fluxer'}
          >
            <FluxerConfigSection
              guild={guild}
              guildId={perms.guildId}
              getAuthHeaders={getAuthHeaders}
              onSaved={setGuild}
            />
          </AccordionCard>
        </>
      )}

      {activeTab === 'features' && perms.isOwner && (
        <>
          <div className="admin-card">
            <div className="admin-card-title">{t('admin.featuresTitle')}</div>
            <div className="admin-card-desc">
              {t('admin.featuresDesc')}
            </div>
            {features === null ? (
              <div className="admin-info">{t('admin.featuresLoading')}</div>
            ) : features.length === 0 ? (
              <div className="admin-info">{t('admin.noFeatures')}</div>
            ) : (
              groupByCategory(features).map(([category, flags]) => (
                <div className="cfg-group" key={category}>
                  <div className="cfg-group-title">
                    {t(`admin.cfgCategory.${category}`, category)}
                  </div>
                  {flags.map((f) => (
                    <FeatureFlagRow
                      key={f.key}
                      flag={f}
                      onToggle={toggleFeature}
                      onSave={saveFeature}
                    />
                  ))}
                </div>
              ))
            )}
            {featuresError && (
              <div className="admin-row">
                <span className="admin-error">{featuresError}</span>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}


/**
 * Discord-bot-invite + setup-checklist.
 *
 * Hämtar bot-client_id + permissions från backend (env-baserat). Saknas
 * DISCORD_BOT_CLIENT_ID visar vi en konfigurationshjälp istället för länken
 * så det är tydligt vad som behöver sättas i stats-sidans env.
 */
function BotInviteSection({ getAuthHeaders }) {
  const { t } = useTranslation();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/admin/discord/invite-info`, {
          headers: getAuthHeaders(),
        });
        if (!cancelled && r.ok) {
          setInfo(await r.json());
        }
      } catch {
        // silent — invite-länken är icke-kritisk
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [getAuthHeaders]);

  const inviteUrl = info?.configured
    ? `https://discord.com/api/oauth2/authorize?client_id=${info.client_id}` +
      `&permissions=${info.permissions}` +
      `&scope=${encodeURIComponent(info.scope || 'bot applications.commands')}`
    : null;

  return (
    <div className="admin-card">
      <div className="admin-card-title">{t('admin.botInviteTitle')}</div>
      <div className="admin-card-desc">
        {t('admin.botInviteDescPre')}<code>/admin register</code>{t('admin.botInviteDescPost')}
      </div>

      {loading ? (
        <div className="admin-info">{t('admin.inviteLoading')}</div>
      ) : inviteUrl ? (
        <div className="admin-bot-invite-row">
          <a
            href={inviteUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="admin-btn admin-bot-invite-btn"
          >
            {t('admin.inviteBtn')}
          </a>
          <input
            className="admin-input admin-bot-invite-url"
            type="text"
            value={inviteUrl}
            readOnly
            onFocus={(e) => e.target.select()}
            aria-label={t('admin.inviteAriaLabel')}
          />
        </div>
      ) : (
        <div className="admin-bot-invite-missing">
          <strong>DISCORD_BOT_CLIENT_ID</strong>{t('admin.inviteMissingPre')}
          <code>DISCORD_BOT_INVITE_PERMISSIONS</code>{t('admin.inviteMissingPost')}
        </div>
      )}

      <ol className="admin-bot-setup-checklist">
        <li>{t('admin.setupStep1')}</li>
        <li>{t('admin.setupStep2')}</li>
        <li>{t('admin.setupStep3Pre')}<code>/admin register</code>{t('admin.setupStep3Post')}</li>
      </ol>
    </div>
  );
}


const VOICE_PLATFORM_OPTIONS = [
  { value: 'discord',   labelKey: 'admin.vpDiscord' },
  { value: 'fluxer',    label: 'Fluxer' },
  { value: 'teamspeak', label: 'TeamSpeak 3' },
  { value: 'mumble',    label: 'Mumble' },
  { value: 'matrix',    label: 'Matrix' },
  { value: 'none',      labelKey: 'admin.vpNone' },
];

/**
 * Voice-plattform-konfiguration för guild — Sprint 4 av Fluxer-migrationen.
 *
 * Default 'discord' = nuvarande beteende oavsett om dropdownen rörs.
 * Andra värden förutsätter att motsvarande adapter är deployad (Fas D).
 * Saveknapp aktiveras bara när användaren faktiskt ändrat värde.
 */
function VoicePlatformSection({ guild, guildId, getAuthHeaders, onSaved }) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState(guild.voice_platform || 'discord');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState('');

  // Synka vid omount eller om guild laddas om externt
  useEffect(() => {
    setSelected(guild.voice_platform || 'discord');
  }, [guild.voice_platform]);

  const current = guild.voice_platform || 'discord';
  const isDirty = selected !== current;

  const handleSave = async () => {
    setSaving(true);
    setErr('');
    setSaved(false);
    try {
      const r = await fetch(`${API_URL}/api/admin/guild/${guildId}`, {
        method: 'PATCH',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ voice_platform: selected }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        setErr(d.detail?.error || d.error || t('settings.couldNotSave'));
      } else {
        const updated = await r.json();
        onSaved && onSaved(updated);
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
      }
    } catch {
      setErr(t('settings.networkError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-card">
      <div className="admin-card-title">{t('admin.voicePlatformTitle')}</div>
      <div className="admin-card-desc">
        {t('admin.voicePlatformDesc')}
      </div>
      <div className="admin-row">
        <label className="admin-label">{t('admin.activePlatformLabel')}</label>
        <select
          className="admin-input"
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
          disabled={saving}
          style={{ maxWidth: 260 }}
        >
          {VOICE_PLATFORM_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.labelKey ? t(opt.labelKey) : opt.label}</option>
          ))}
        </select>
      </div>
      {isDirty && (
        <div className="admin-card-desc" style={{ color: '#f5c518', marginTop: '0.5rem' }}>
          ⚠️ {t('admin.platformDirtyWarn')}
        </div>
      )}
      <div className="admin-row" style={{ marginTop: '1rem' }}>
        <button
          className="admin-btn"
          onClick={handleSave}
          disabled={saving || !isDirty}
        >
          {saving ? t('settings.saving') : t('settings.save')}
        </button>
        {saved && <span className="admin-success">{t('admin.saved')}</span>}
        {err && <span className="admin-error">{err}</span>}
      </div>
    </div>
  );
}


/**
 * BOT_ENABLED-toggle (PR S) — global on/off för Discord-bot:s event-loop.
 *
 * När false hoppar bot:en över match-events utan att containern stoppas.
 * Slash-commands fortsätter fungera så länge bot är ansluten till Discord.
 * Toggle pollas av bot var 30:e sekund — räkna med ~30s eftersläp.
 */
function BotEnabledToggle({ getAuthHeaders }) {
  const { t } = useTranslation();
  const [enabled, setEnabled] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/bot/state/`, {
          headers: getAuthHeaders(),
        });
        if (!r.ok) {
          if (!cancelled) setErr(t('admin.couldNotFetchStatus'));
          return;
        }
        const d = await r.json();
        if (!cancelled) setEnabled(!!d.enabled);
      } catch {
        if (!cancelled) setErr(t('settings.networkError'));
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getAuthHeaders]);

  const toggle = async () => {
    if (enabled === null) return;
    setSaving(true);
    setErr('');
    try {
      const r = await fetch(`${API_URL}/api/admin/bot/state/`, {
        method: 'PATCH',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: !enabled }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        setErr(d.error || t('admin.couldNotChange'));
      } else {
        const d = await r.json();
        setEnabled(!!d.enabled);
      }
    } catch {
      setErr(t('settings.networkError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="admin-row"
      style={{ marginBottom: '0.75rem', padding: '0.5rem', background: 'rgba(0,0,0,0.15)', borderRadius: 4 }}
    >
      <div style={{ flex: 1 }}>
        <strong>{t('admin.botEventLoop')}</strong>
        <div className="admin-card-desc" style={{ marginTop: 2 }}>
          {t('admin.botEventLoopDesc')}
        </div>
      </div>
      <button
        className="admin-btn"
        onClick={toggle}
        disabled={saving || enabled === null}
        style={{
          background: enabled === false ? '#c33' : enabled === true ? '#3a7' : undefined,
          minWidth: 80,
        }}
      >
        {enabled === null ? '…' : enabled ? t('veto.on') : t('veto.off')}
      </button>
      {err && <span className="admin-error" style={{ marginLeft: 8 }}>{err}</span>}
    </div>
  );
}


/**
 * Discord-kanal-konfiguration — speglar TS3/Fluxer-mönstret med egen
 * form-state + Spara-knapp. Innan UI-refactor:n låg dessa fält inline i
 * AdminSettings vilket bröt symmetrin mellan voice-platforms.
 */
function DiscordConfigSection({ guild, guildId, getAuthHeaders, onSaved }) {
  const { t } = useTranslation();
  const initialForm = () => {
    const f = {};
    CHANNEL_FIELDS.forEach((field) => { f[field.key] = guild[field.key] || ''; });
    return f;
  };
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState('');

  // Synka när guild laddas om externt
  useEffect(() => {
    setForm(initialForm());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guild]);

  const updateField = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setSaved(false);
  };

  const hasChanges = CHANNEL_FIELDS.some((f) => form[f.key] !== (guild[f.key] || ''));

  const handleSave = async () => {
    setSaving(true);
    setErr('');
    setSaved(false);
    try {
      const r = await fetch(`${API_URL}/api/admin/guild/${guildId}`, {
        method: 'PATCH',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        setErr(d.detail?.error || d.error || t('settings.couldNotSave'));
      } else {
        const updated = await r.json();
        onSaved && onSaved(updated);
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
      }
    } catch {
      setErr(t('settings.networkError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-card">
      <BotEnabledToggle getAuthHeaders={getAuthHeaders} />
      <div className="admin-card-title">{t('admin.discordChannelsTitle')}</div>
      <div className="admin-card-desc">
        {t('admin.discordChannelsDescPre')}<code>DISCORD_BOT_TOKEN</code>{t('admin.discordChannelsDescPost')}
      </div>
      {CHANNEL_FIELDS.map((field) => (
        <div className="admin-row" key={field.key}>
          <label className="admin-label" title={t(field.descKey)}>{t(field.labelKey)}</label>
          <input
            className="admin-input"
            placeholder={t('admin.channelIdPlaceholder')}
            value={form[field.key] || ''}
            onChange={(e) => updateField(field.key, e.target.value)}
            disabled={saving}
            style={{ maxWidth: 250, fontFamily: 'monospace', fontSize: '0.8rem' }}
          />
        </div>
      ))}
      <div className="admin-row admin-ts3-actions">
        <button
          className="admin-btn"
          onClick={handleSave}
          disabled={saving || !hasChanges}
        >
          {saving ? t('settings.saving') : t('settings.save')}
        </button>
        {saved && <span className="admin-success">{t('admin.saved')}</span>}
        {err && <span className="admin-error">{err}</span>}
      </div>
    </div>
  );
}


/**
 * TS3-konfigurationssektion — Spår 2b Fas 9 av Fluxer-migrationen.
 *
 * Visar bara de skarpa fälten: connection (host/port/user/pass/sid) +
 * routing (lobby, match_category). Team1/Team2/Stats/Queue/Tournament-
 * channel-fälten togs bort i Fas 9 / migration 0031 — dynamiska kanaler
 * skapas per match under match_category, statisk lobby är destination
 * efter match.
 *
 * "Testa anslutning" anropar GET /api/voice/teamspeak/test-connection/
 * och visar grön/röd status-ruta baserat på {ok, server_name, client_count}.
 * Tom config = env-fallback (.env: TS3_HOST etc.).
 */
function TS3ConfigSection({ guild, guildId, getAuthHeaders, onSaved }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    ts3_host: guild.ts3_host || '',
    ts3_query_port: guild.ts3_query_port ?? '',
    ts3_query_user: guild.ts3_query_user || '',
    ts3_query_pass: '',
    ts3_virtual_server_id: guild.ts3_virtual_server_id ?? '',
    ts3_lobby_channel: guild.ts3_lobby_channel || '',
    ts3_match_category: guild.ts3_match_category || '',
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  // Synka när guild laddas om externt — bevarar inte password (write-only)
  useEffect(() => {
    setForm((prev) => ({
      ts3_host: guild.ts3_host || '',
      ts3_query_port: guild.ts3_query_port ?? '',
      ts3_query_user: guild.ts3_query_user || '',
      ts3_query_pass: prev.ts3_query_pass,  // användarens pågående input
      ts3_virtual_server_id: guild.ts3_virtual_server_id ?? '',
      ts3_lobby_channel: guild.ts3_lobby_channel || '',
      ts3_match_category: guild.ts3_match_category || '',
    }));
  }, [guild]);

  const updateField = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setSaved(false);
    setTestResult(null);
  };

  const buildPatchBody = () => {
    const body = {};
    Object.entries(form).forEach(([k, v]) => {
      if (k === 'ts3_query_pass') {
        if (v) body[k] = v;  // bara skicka om användaren skrivit nytt
      } else if (k === 'ts3_query_port' || k === 'ts3_virtual_server_id') {
        body[k] = v === '' ? null : Number(v);
      } else {
        body[k] = v || '';
      }
    });
    return body;
  };

  const handleSave = async () => {
    setSaving(true);
    setErr('');
    setSaved(false);
    setTestResult(null);
    try {
      const r = await fetch(`${API_URL}/api/admin/guild/${guildId}`, {
        method: 'PATCH',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(buildPatchBody()),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        setErr(d.detail?.error || d.error || t('settings.couldNotSave'));
      } else {
        const updated = await r.json();
        onSaved && onSaved(updated);
        // Rensa password-fältet efter lyckad spar
        setForm((f) => ({ ...f, ts3_query_pass: '' }));
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
      }
    } catch {
      setErr(t('settings.networkError'));
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    setErr('');
    try {
      const r = await fetch(
        `${API_URL}/api/voice/teamspeak/test-connection/${guildId}`,
        { headers: getAuthHeaders() },
      );
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        setErr(data.detail?.error || data.error || `HTTP ${r.status}`);
      } else {
        setTestResult(data);
      }
    } catch {
      setErr(t('settings.networkError'));
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="admin-card">
      <div className="admin-card-title">{t('admin.ts3Title')}</div>
      <div className="admin-card-desc">
        {t('admin.ts3Desc')}
      </div>

      <div className="admin-row admin-ts3-grid">
        <div className="admin-ts3-field">
          <label className="admin-label">Host</label>
          <input
            type="text"
            className="admin-input"
            value={form.ts3_host}
            onChange={(e) => updateField('ts3_host', e.target.value)}
            placeholder={t('admin.ts3HostPlaceholder')}
            disabled={saving}
          />
        </div>
        <div className="admin-ts3-field admin-ts3-field-small">
          <label className="admin-label">{t('admin.ts3QueryPort')}</label>
          <input
            type="number"
            className="admin-input"
            value={form.ts3_query_port}
            onChange={(e) => updateField('ts3_query_port', e.target.value)}
            placeholder="10011"
            disabled={saving}
          />
        </div>
        <div className="admin-ts3-field">
          <label className="admin-label">{t('admin.ts3QueryUser')}</label>
          <input
            type="text"
            className="admin-input"
            value={form.ts3_query_user}
            onChange={(e) => updateField('ts3_query_user', e.target.value)}
            placeholder="serveradmin"
            disabled={saving}
          />
        </div>
        <div className="admin-ts3-field">
          <label className="admin-label">{t('admin.ts3QueryPass')}</label>
          <input
            type="password"
            className="admin-input"
            value={form.ts3_query_pass}
            onChange={(e) => updateField('ts3_query_pass', e.target.value)}
            placeholder={t('admin.passUnchangedPlaceholder')}
            disabled={saving}
            autoComplete="new-password"
          />
        </div>
        <div className="admin-ts3-field admin-ts3-field-small">
          <label className="admin-label">{t('admin.ts3VirtualServerId')}</label>
          <input
            type="number"
            className="admin-input"
            value={form.ts3_virtual_server_id}
            onChange={(e) => updateField('ts3_virtual_server_id', e.target.value)}
            placeholder="1"
            disabled={saving}
          />
        </div>
        <div className="admin-ts3-field">
          <label className="admin-label">{t('admin.ts3LobbyChannel')}</label>
          <input
            type="text"
            className="admin-input"
            value={form.ts3_lobby_channel}
            onChange={(e) => updateField('ts3_lobby_channel', e.target.value)}
            placeholder={t('admin.ts3LobbyPlaceholder')}
            disabled={saving}
          />
        </div>
        <div className="admin-ts3-field">
          <label className="admin-label">{t('admin.ts3MatchCategory')}</label>
          <input
            type="text"
            className="admin-input"
            value={form.ts3_match_category}
            onChange={(e) => updateField('ts3_match_category', e.target.value)}
            placeholder={t('admin.ts3MatchCategoryPlaceholder')}
            disabled={saving}
          />
        </div>
      </div>

      {testResult && testResult.ok && (
        <div className="admin-ts3-test-ok">
          ✓ {t('admin.connectedTo')} <strong>{testResult.server_name}</strong> · {testResult.client_count} {t('admin.playersOnline')}
          {' '}<span className="admin-ts3-test-meta">
            ({testResult.host}:{testResult.port}, sid={testResult.virtual_server_id})
          </span>
        </div>
      )}
      {testResult && !testResult.ok && (
        <div className="admin-ts3-test-fail">
          ✗ {t('admin.connectFailed')} <code>{testResult.error}</code>
        </div>
      )}

      <div className="admin-row admin-ts3-actions">
        <button
          className="admin-btn"
          onClick={handleSave}
          disabled={saving || testing}
        >
          {saving ? t('settings.saving') : t('settings.save')}
        </button>
        <button
          className="admin-btn admin-btn-secondary"
          onClick={handleTestConnection}
          disabled={saving || testing}
          type="button"
        >
          {testing ? t('admin.testing') : t('admin.testConnection')}
        </button>
        {saved && <span className="admin-success">{t('admin.saved')}</span>}
        {err && <span className="admin-error">{err}</span>}
      </div>
    </div>
  );
}


/**
 * Fluxer-konfigurationssektion — Spår 3 av Fluxer-migrationen.
 *
 * Speglar TS3ConfigSection exakt men med Fluxer-fält:
 *   - base_url + bot_token + guild_id (community-snowflake)
 *   - lobby_channel + match_category (Fluxer voice-channel-snowflakes)
 *
 * Bot-token är write-only — fältet är tomt vid load, fyll i för att byta.
 * Tom config = env-fallback (.env: FLUXER_BASE_URL/FLUXER_BOT_TOKEN/FLUXER_GUILD_ID).
 *
 * "Testa anslutning" anropar GET /api/voice/fluxer/test-connection/<guild_pk>
 * (utan trailing slash — se kommentar vid fetch) och visar grön/röd status-ruta
 * baserat på {ok, guild_name, base_url, fluxer_guild_id}.
 */
function FluxerConfigSection({ guild, guildId, getAuthHeaders, onSaved }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    fluxer_base_url: guild.fluxer_base_url || '',
    fluxer_bot_token: '',
    fluxer_guild_id: guild.fluxer_guild_id || '',
    fluxer_lobby_channel: guild.fluxer_lobby_channel || '',
    fluxer_match_category: guild.fluxer_match_category || '',
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  // Synka när guild laddas om externt — bevarar inte bot_token (write-only)
  useEffect(() => {
    setForm((prev) => ({
      fluxer_base_url: guild.fluxer_base_url || '',
      fluxer_bot_token: prev.fluxer_bot_token,  // användarens pågående input
      fluxer_guild_id: guild.fluxer_guild_id || '',
      fluxer_lobby_channel: guild.fluxer_lobby_channel || '',
      fluxer_match_category: guild.fluxer_match_category || '',
    }));
  }, [guild]);

  const updateField = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setSaved(false);
    setTestResult(null);
  };

  const buildPatchBody = () => {
    const body = {};
    Object.entries(form).forEach(([k, v]) => {
      if (k === 'fluxer_bot_token') {
        if (v) body[k] = v;  // bara skicka om användaren skrivit nytt
      } else {
        body[k] = v || '';
      }
    });
    return body;
  };

  const handleSave = async () => {
    setSaving(true);
    setErr('');
    setSaved(false);
    setTestResult(null);
    try {
      const r = await fetch(`${API_URL}/api/admin/guild/${guildId}`, {
        method: 'PATCH',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(buildPatchBody()),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        setErr(d.detail?.error || d.error || t('settings.couldNotSave'));
      } else {
        const updated = await r.json();
        onSaved && onSaved(updated);
        setForm((f) => ({ ...f, fluxer_bot_token: '' }));
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
      }
    } catch {
      setErr(t('settings.networkError'));
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    setErr('');
    try {
      // OBS: ingen trailing slash — FastAPI-routen är slash-lös och en
      // trailing slash ger en 307-redirect till container-intern host
      // (http://localhost:8001/...) som webbläsaren inte når → "nätverksfel".
      // Matchar teamspeak-anropet ovan; proxyn lägger själv på Django-slashen.
      const r = await fetch(
        `${API_URL}/api/voice/fluxer/test-connection/${guildId}`,
        { headers: getAuthHeaders() },
      );
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        setErr(data.detail?.error || data.error || `HTTP ${r.status}`);
      } else {
        setTestResult(data);
      }
    } catch {
      setErr(t('settings.networkError'));
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="admin-card">
      <div className="admin-card-title">{t('admin.fluxerTitle')}</div>
      <div className="admin-card-desc">
        {t('admin.fluxerDesc')}
      </div>

      <div className="admin-row admin-ts3-grid">
        <div className="admin-ts3-field">
          <label className="admin-label">Base URL</label>
          <input
            type="text"
            className="admin-input"
            value={form.fluxer_base_url}
            onChange={(e) => updateField('fluxer_base_url', e.target.value)}
            placeholder={t('admin.fluxerBaseUrlPlaceholder')}
            disabled={saving}
          />
        </div>
        <div className="admin-ts3-field">
          <label className="admin-label">{t('admin.fluxerBotToken')}</label>
          <input
            type="password"
            className="admin-input"
            value={form.fluxer_bot_token}
            onChange={(e) => updateField('fluxer_bot_token', e.target.value)}
            placeholder={t('admin.passUnchangedPlaceholder')}
            disabled={saving}
            autoComplete="new-password"
          />
        </div>
        <div className="admin-ts3-field">
          <label className="admin-label">{t('admin.fluxerCommunityId')}</label>
          <input
            type="text"
            className="admin-input"
            value={form.fluxer_guild_id}
            onChange={(e) => updateField('fluxer_guild_id', e.target.value)}
            placeholder={t('admin.fluxerGuildIdPlaceholder')}
            disabled={saving}
          />
        </div>
        <div className="admin-ts3-field">
          <label className="admin-label">{t('admin.fluxerLobbyChannel')}</label>
          <input
            type="text"
            className="admin-input"
            value={form.fluxer_lobby_channel}
            onChange={(e) => updateField('fluxer_lobby_channel', e.target.value)}
            placeholder={t('admin.fluxerLobbyPlaceholder')}
            disabled={saving}
          />
        </div>
        <div className="admin-ts3-field">
          <label className="admin-label">{t('admin.fluxerMatchCategory')}</label>
          <input
            type="text"
            className="admin-input"
            value={form.fluxer_match_category}
            onChange={(e) => updateField('fluxer_match_category', e.target.value)}
            placeholder={t('admin.fluxerMatchCategoryPlaceholder')}
            disabled={saving}
          />
        </div>
      </div>

      {testResult && testResult.ok && (
        <div className="admin-ts3-test-ok">
          ✓ {t('admin.connectedTo')} <strong>{testResult.guild_name}</strong>
          {' '}<span className="admin-ts3-test-meta">
            ({testResult.base_url}, community={testResult.fluxer_guild_id})
          </span>
        </div>
      )}
      {testResult && !testResult.ok && (
        <div className="admin-ts3-test-fail">
          ✗ {t('admin.connectFailed')} <code>{testResult.error}</code>
        </div>
      )}

      <div className="admin-row admin-ts3-actions">
        <button
          className="admin-btn"
          onClick={handleSave}
          disabled={saving || testing}
        >
          {saving ? t('settings.saving') : t('settings.save')}
        </button>
        <button
          className="admin-btn admin-btn-secondary"
          onClick={handleTestConnection}
          disabled={saving || testing}
          type="button"
        >
          {testing ? t('admin.testing') : t('admin.testConnection')}
        </button>
        {saved && <span className="admin-success">{t('admin.saved')}</span>}
        {err && <span className="admin-error">{err}</span>}
      </div>
    </div>
  );
}
